import { isEnabled } from '../../../../shared/featureFlags.js';
import AuthController from '../services/AuthController.js';
import { guardedFetch } from './navGeneration.js';

export const magnetOverviewEnabled = () => import.meta.env.VITE_MAGNET_OVERVIEW === 'true'
  || isEnabled('FF_M5M_MAGNET_OVERVIEW');
export const magnetHomePath = (guid, section = 'overview') => `/magnets/${encodeURIComponent(guid)}${section === 'overview' ? '' : `/${section}`}`;
export const publication = status => ({ ENABLED: 'Published', DRAFT: 'Draft', DISABLED: 'Paused' })[status] || 'Status unavailable';
export const countLabel = (count, singular, plural = `${singular}s`) => `${Number(count).toLocaleString()} ${Number(count) === 1 ? singular : plural}`;
export const overviewScope = () => JSON.stringify([AuthController.getEntity?.()?.guid, AuthController.getStoredActiveOrg?.(), AuthController.getScope?.()?.activeOrg?.guid]);

export async function magnetRequest(path, options) {
  const response = await guardedFetch(`${AuthController.getConfig().BASE_URL}/m5t/v5/magnets${path}`, options);
  const data = await response.json().catch(() => null);
  if (!response.ok || data?.ok !== true) {
    const error = new Error(response.status === 409 ? 'This magnet changed in another window. Reload it and review the latest websites before trying again.'
      : data?.message || data?.error || 'This information could not be loaded. Try again.');
    error.status = response.status;
    throw error;
  }
  return data;
}

export async function readMagnet(guid) {
  const data = await magnetRequest(`/${encodeURIComponent(guid)}`, { cache: 'no-store' });
  if (data.definition_read_version !== 1 || data.magnet?.guid !== guid) throw new Error('This magnet could not be confirmed. Reload and try again.');
  return data.magnet;
}

// Never base a replacement domain list on a cached row. The save still checks the
// revision under the existing app/conversation locks if another edit follows this read.
export async function approveMagnetDomain(guid, domain, request = magnetRequest) {
  const data = await request(`/${encodeURIComponent(guid)}`, { cache: 'no-store' });
  const current = data.magnet;
  if (data.definition_read_version !== 1 || current?.guid !== guid || !current.can_manage || !current.revision || !Array.isArray(current.domains)) {
    throw new Error('The latest websites could not be confirmed. Reload before allowing a website.');
  }
  const add = String(domain || '').trim().toLowerCase();
  if (!add) throw new Error('Choose a website to allow.');
  if (current.domains.includes(add)) return { ok: true, unchanged: true };
  return request(`/${encodeURIComponent(guid)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ save_version: 1, expected_revision: current.revision, domains: [...new Set([...current.domains, add])] }),
  });
}

// A failed refresh keeps the last observation and its timestamp; a failed initial
// request has no value. Callers render those cases separately from real zeroes.
export async function refreshObservation(previous, read, validate = () => true) {
  try {
    const value = await read();
    if (!validate(value)) throw new Error('The response was incomplete. Try again.');
    return { status: 'ready', value, checkedAt: Date.now() };
  } catch (error) {
    return { ...previous, status: previous?.value ? 'stale' : 'unavailable', error: error.message };
  }
}

const returns = new Map();
export function rememberMagnetOrigin(guid, path) {
  if (!['/magnets', ...['overview', 'website', 'results'].map(section => magnetHomePath(guid, section))].includes(path)) return;
  returns.set(`${overviewScope()}:${guid}`, path);
}
export const magnetOrigin = guid => returns.get(`${overviewScope()}:${guid}`) || '/magnets';

const HOME_RETURN_KEY = 'm5m_magnet_home_return';
export const validHomePath = path => /^\/magnets\/(?!new(?:\/|$)|drafts(?:\/|$)|archived(?:\/|$))[\w-]+(?:\/(?:website|results))?$/.test(path);
export function rememberMagnetHomeReturn(hash) {
  const path = String(hash || '').replace(/^#/, '');
  if (!magnetOverviewEnabled() || !validHomePath(path)) return;
  try { sessionStorage.setItem(HOME_RETURN_KEY, JSON.stringify({ path, at: Date.now() })); } catch { /* unavailable storage */ }
}
export function takeMagnetHomeReturn() {
  try {
    const value = JSON.parse(sessionStorage.getItem(HOME_RETURN_KEY) || 'null'); sessionStorage.removeItem(HOME_RETURN_KEY);
    return magnetOverviewEnabled() && validHomePath(value?.path) && Date.now() - value.at < 30 * 60 * 1000 ? value.path : null;
  } catch { return null; }
}
export function clearMagnetHomeReturn() { try { sessionStorage.removeItem(HOME_RETURN_KEY); } catch { /* unavailable storage */ } }
